import { useMemo, useRef, useState } from "react";
import { Box, Input, InputGroup, InputRightElement, List, ListItem, Spinner, IconButton, useOutsideClick, } from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { Controller, type Control, type FieldPath, type FieldValues } from "react-hook-form";
export type TAOption = { id: number | string; label: string };

type Props<TFieldValues extends FieldValues, TTransformedValues = TFieldValues> = {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues, any, TTransformedValues>;
    options: any[];
    onCompletion: (query?: string) => void;
    loading: boolean;
    val?: any;
    optionValue?: string;
    optionLabel?: string;
    placeholder?: string;
    disabled?: boolean;
};

function Typeahead<TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({ name, control, options, onCompletion, loading, val, optionValue = 'id', optionLabel = 'name', placeholder = 'Search...', disabled }: Props<TFieldValues, TTransformedValues>) {
    const [input, setInput] = useState<string>('');
    const [showOptions, setShowOptions] = useState(false);
    const [value, setValue] = useState<any>(val);
    const rootRef = useRef<HTMLDivElement>(null);

    useOutsideClick({
        ref: rootRef,
        handler: () => {
            setShowOptions(false);
            if (!value) setInput('');
        }
    });

    const showClear = useMemo(() => !!input && !disabled, [input, disabled]);

    function onInputChange(query?: string) {
        setValue(undefined);
        if (showOptions) {
            onCompletion(query);
        }
    }

    function handleClear() {
        setInput('');
        setShowOptions(false);
        setValue(undefined);
    }

    function handleSelect(_value: any) {
        setValue(_value);
        setInput(_value[optionLabel]);
        setShowOptions(false);
    }

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                return <Box ref={rootRef} position="relative" w="100%">
                    {/* <div>value: {field.value ? `${field.value[optionValue]} - ${field.value[optionLabel]}` : 'none'}</div> */}
                    <InputGroup>
                        <Input
                            value={value ? value[optionLabel] : input}
                            placeholder={placeholder}
                            onChange={(e) => {
                                const q = e.target.value;
                                setInput(q);
                                onInputChange(q);
                                field.onChange(undefined);
                            }}
                            onFocus={() => {
                                setShowOptions(true);
                            }}
                            disabled={disabled}
                            autoComplete="off"
                        />
                        <InputRightElement width="3rem">
                            {loading ? (
                                <Spinner size="sm" />
                            ) : showClear ? (
                                <IconButton
                                    aria-label="Clear"
                                    size="sm"
                                    variant="ghost"
                                    icon={<CloseIcon boxSize={2.5} />}
                                    onClick={() => { handleClear(); field.onChange(undefined); }}
                                />
                            ) : null}
                        </InputRightElement>
                    </InputGroup>

                    {showOptions && options.length > 0 && (
                        <List
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            bg="white"
                            borderWidth="1px"
                            borderRadius="md"
                            mt={1}
                            maxH="220px"
                            overflowY="auto"
                            zIndex={10}
                            boxShadow="sm"
                        >
                            {options.map((opt) => (
                                <ListItem
                                    key={opt[optionValue]}
                                    px={3}
                                    py={2}
                                    _hover={{ bg: "gray.100", cursor: "pointer" }}
                                    onClick={() => { handleSelect(opt); field.onChange(opt); }}
                                >
                                    {opt[optionLabel]}
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            }}
        />
    );
}

export default Typeahead;
